import clsx from 'clsx';
import ThemeProvider from '@pixum/theme-provider';
import './styles.css';
import { CONTENTFUL_CARD_TYPES } from 'data-layer/constants';
import { NavigationCard, CardGroup, ProductCard } from './variants';
import { isSwipeable, isTwoColumnGrid } from '../../utils';
import type { CardCollectionProps, CardItem } from './types';
import type { CardNode, GroupedCardNode, ContentfulMobilePresentationType, Device } from 'data-layer';

function isGroupedCardNode(card: CardItem): card is GroupedCardNode {
  return !Array.isArray(card) && card.transformerType === 'GroupedCardTransformer';
}

function isCardNodeArray(card: CardItem): card is CardNode[] {
  return Array.isArray(card);
}

/**
 * Determines the appropriate CSS class for the card grid layout based on the provided layout and device type. This
 * function checks if the device is mobile and then evaluates the layout type to return the corresponding CSS
 * class for swipeable or two-column grid layouts. Please keep in mind that the swipeable class is the default
 * behaviour.
 *
 * @param layout - The layout configuration, which can be a string or a value from ContentfulMobilePresentationType, indicating the desired presentation style for mobile devices.
 * @param device - The type of device being used (e.g., 'mobile', 'desktop') to determine if the layout adjustments should be applied.
 * @returns A string representing the CSS class to be applied to the card grid wrapper, based on the layout and device type. If the device is not mobile or the layout does not match the specified types, an empty string is returned.
 */
function getCardGridLayoutClassForMobileDevice(
  layout: ContentfulMobilePresentationType | string,
  device: Device
): string {
  if (device === 'mobile') {
    if (isSwipeable(layout)) {
      return 'grid__wrapper--swipeable';
    }

    if (isTwoColumnGrid(layout)) {
      return 'grid__wrapper--two-columns';
    }
  }

  return '';
}

export function CardCollection(props: CardCollectionProps) {
  const { cards, theme, type, layout, device } = props;

  return (
    <ThemeProvider theme={theme}>
      <ul className={clsx('grid__wrapper', getCardGridLayoutClassForMobileDevice(layout, device))}>
        {cards.map((card, index) => {
          const key = `card-collection-${index}`;

          if (isCardNodeArray(card)) {
            return <CardGroup cards={card} key={key} device={device} layout={layout} />;
          }

          if (isGroupedCardNode(card)) {
            return <CardGroup cards={[card]} key={key} device={device} layout={layout} />;
          }

          const cardNode = card as CardNode;

          switch (type) {
            case CONTENTFUL_CARD_TYPES.NAVIGATION:
              return <NavigationCard key={key} {...cardNode} wrapperTag="li" device={device} layout={layout} />;
            case CONTENTFUL_CARD_TYPES.PRODUCT:
              return <ProductCard {...cardNode} key={key} wrapperTag="li" device={device} layout={layout} />;
            default:
              return null;
          }
        })}
      </ul>
    </ThemeProvider>
  );
}
